import { ShowSection } from 'framework/ui/layout';
import { Stack, Typography, Box } from '@mui/material';
import { useCallback, useState } from 'react';
import { Button } from 'framework/ui/mui-builder';
import { Modal } from 'framework/ui/ui-builder';
import { ModalForm, TextArea } from 'framework/form';
import useProjectCycleService from '../useProjectCycleService';
import { useShowContext } from 'framework/show';
import type { Data } from 'framework/types';
import {
  ACCESS_INFO_ACTIONS,
  ACCESS_INFO_SECTIONS,
  CYCLE_SETTINGS_STATUS,
  useProjectSecurityContext,
} from 'domain/shared/newProject';
import { useMessageService, ErrorMessage, SuccessMessage } from 'framework/ui/Message';
import { EmployeeLinkFieldWithNameLoading } from 'domain/shared/employee';

const CycleApproveAnnotation = ({ projectId }: { projectId: string }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const { approveCycleSettings, rejectCycleSettings, getRejectValidationSchema } = useProjectCycleService();
  const { data, setData } = useShowContext();
  const { isAccessGranted } = useProjectSecurityContext();
  const { addMessage } = useMessageService();

  const applyUpdatedSettings = useCallback(
    (updatedSettings: Data) => {
      if (!updatedSettings?.cycleActivitySettings) return;
      setData?.((prev: Data) => ({ ...prev, settings: updatedSettings }));
    },
    [setData],
  );

  const getIsVisible = useCallback(
    (showSectionData: Data) => {
      return (
        isAccessGranted(ACCESS_INFO_SECTIONS.SDLC, ACCESS_INFO_ACTIONS.REVIEW) &&
        showSectionData?.settings?.cycleActivitySettings?.cycleSettingsStatus === CYCLE_SETTINGS_STATUS.IN_APPROVAL
      );
    },
    [isAccessGranted],
  );

  const handleApprove = async () => {
    try {
      const updatedSettings = await approveCycleSettings(projectId, { projectId });
      addMessage(new SuccessMessage('Settings approved'));
      applyUpdatedSettings(updatedSettings);
    } catch (error) {
      addMessage(new ErrorMessage('Settings have not been approved'));
    }
  };

  return (
    <ShowSection title="Cycles management approval" titleVariant={'h5'} isVisible={getIsVisible}>
      <Stack gap={2}>
        <Typography color="textSecondary" flexDirection="row" display="flex" gap={1}>
          Project Manager <EmployeeLinkFieldWithNameLoading label="" value={data?.projectManager} /> has requested
          <b>biweekly</b> cycle management for project {data?.name} by the following reason:
        </Typography>
        <Typography>{data?.settings?.cycleActivitySettings?.cycleChangeReason || '/not defined/'}</Typography>
      </Stack>
      <Box display="flex" flexDirection="row" alignItems="center" justifyContent="flex-end" gap={2} pt={1} pb={1}>
        <Button variant="outlined" onClick={() => setModalOpen(true)}>
          Reject
        </Button>
        <Button variant="contained" onClick={handleApprove}>
          Approve
        </Button>
      </Box>
      <Modal isOpen={modalOpen} onToggle={() => setModalOpen(false)} title="Reason for rejection">
        <ModalForm
          closeModal={() => setModalOpen(false)}
          modalOpen={modalOpen}
          submitFormFunc={values =>
            rejectCycleSettings(projectId, {
              projectId: projectId,
              cycleRejectionReason: values.cycleRejectionReason,
            })
          }
          getValidationSchemaFunc={getRejectValidationSchema}
          afterSubmit={{
            execute: (updatedSettings: Data) => {
              applyUpdatedSettings(updatedSettings);
            },
          }}
        >
          <TextArea
            sx={{ backgroundColor: 'background.paper' }}
            source="cycleRejectionReason"
            label=""
            placeholder="Please, provide reason here"
          />
        </ModalForm>
      </Modal>
    </ShowSection>
  );
};

export default CycleApproveAnnotation;
